import { breakpointSizes } from 'components/utils/breakpoint'
import { type RefObject, useLayoutEffect, useState } from 'react'

const MOBILE_MAX_WIDTH_PX = breakpointSizes.mobile.max

function toHTMLElement(element: Element | null | undefined): HTMLElement | null {
  return element instanceof HTMLElement ? element : null
}

function getMaxEndClusterWidth(
  utilBarEl: HTMLElement,
  startEl: HTMLElement | null,
): number {
  const utilBarStyle = window.getComputedStyle(utilBarEl)
  const paddingX = parseFloat(utilBarStyle.paddingLeft) + parseFloat(utilBarStyle.paddingRight)
  const utilBarGap = parseFloat(utilBarStyle.columnGap || utilBarStyle.gap) || 0
  const startWidth = startEl?.getBoundingClientRect().width ?? 0

  const centerEl = toHTMLElement(utilBarEl.querySelector('[data-util-bar-center]'))
  const centerDisplayed = centerEl && window.getComputedStyle(centerEl).display !== 'none'
  const centerWidth = centerDisplayed ? centerEl.getBoundingClientRect().width : 0
  const flexGaps = centerDisplayed ? utilBarGap * 2 : utilBarGap

  return utilBarEl.clientWidth - paddingX - startWidth - centerWidth - flexGaps
}

function getMeasureSummaryWidth(measureEl: HTMLElement): number {
  const pointsRow = toHTMLElement(measureEl.querySelector('[data-measure-points]'))
  const creditRow = toHTMLElement(measureEl.querySelector('[data-measure-credit]'))

  if (pointsRow && creditRow) {
    return Math.max(pointsRow.scrollWidth, creditRow.scrollWidth)
  }

  return measureEl.scrollWidth
}

function measureExpandedAccountFits(
  measureEl: HTMLElement,
  accountButtonEl: HTMLElement,
): boolean {
  const utilBarEl = toHTMLElement(accountButtonEl.closest('[data-util-bar]'))
  const startEl = toHTMLElement(utilBarEl?.querySelector('[data-util-bar-start]'))
  const endEl = toHTMLElement(accountButtonEl.closest('[data-util-bar-end]'))

  if (!endEl) {
    return true
  }

  const endStyle = window.getComputedStyle(endEl)
  const gap = parseFloat(endStyle.columnGap || endStyle.gap) || 0

  let siblingsWidth = 0
  for (const child of endEl.children) {
    if (!(child instanceof HTMLElement)) {
      continue
    }

    if (child.contains(accountButtonEl)) {
      continue
    }
    siblingsWidth += child.getBoundingClientRect().width
  }

  const avatarEl = accountButtonEl.querySelector('[data-nav-account-avatar]')
  const avatarWidth = avatarEl?.getBoundingClientRect().width ?? 0
  const buttonStyle = window.getComputedStyle(accountButtonEl)
  const paddingX = parseFloat(buttonStyle.paddingLeft) + parseFloat(buttonStyle.paddingRight)
  const buttonGap = parseFloat(buttonStyle.gap) || 0
  const nameWidth = measureEl.querySelector('[data-measure-name]')?.scrollWidth ?? 0
  const summaryWidth = getMeasureSummaryWidth(measureEl)
  const labelsWidth = Math.max(nameWidth, summaryWidth)
  const fullAccountWidth = avatarWidth + buttonGap + labelsWidth + paddingX
  const gapsBetween = Math.max(0, endEl.children.length - 1) * gap
  const totalNeeded = siblingsWidth + fullAccountWidth + gapsBetween

  const availableWidth = utilBarEl
    ? Math.max(endEl.clientWidth, getMaxEndClusterWidth(utilBarEl, startEl))
    : endEl.clientWidth

  return totalNeeded <= availableWidth + 1
}

function getUtilBarClusterGapPx(utilBarEl: HTMLElement): number {
  const utilBarStyle = window.getComputedStyle(utilBarEl)
  return parseFloat(utilBarStyle.columnGap || utilBarStyle.gap) || 0
}

function endClusterOverlapsStart(accountButtonEl: HTMLElement): boolean {
  const utilBarEl = toHTMLElement(accountButtonEl.closest('[data-util-bar]'))
  const startEl = toHTMLElement(utilBarEl?.querySelector('[data-util-bar-start]'))
  const endEl = toHTMLElement(accountButtonEl.closest('[data-util-bar-end]'))

  if (!utilBarEl || !startEl || !endEl) {
    return false
  }

  const startRect = startEl.getBoundingClientRect()
  const minGap = getUtilBarClusterGapPx(utilBarEl)
  let leftmostEdge = Infinity

  for (const child of endEl.children) {
    if (!(child instanceof HTMLElement)) {
      continue
    }

    if (window.getComputedStyle(child).display === 'none') {
      continue
    }

    const childRect = child.getBoundingClientRect()

    if (childRect.width === 0) {
      continue
    }

    leftmostEdge = Math.min(leftmostEdge, childRect.left)
  }

  if (!Number.isFinite(leftmostEdge)) {
    return false
  }

  return leftmostEdge < startRect.right + minGap - 1
}

function accountSummarySpillsUtilBar(accountButtonEl: HTMLElement): boolean {
  const utilBarEl = toHTMLElement(accountButtonEl.closest('[data-util-bar]'))
  const labelsEl = toHTMLElement(accountButtonEl.querySelector('[data-nav-account-labels]'))

  if (!utilBarEl || !labelsEl || window.getComputedStyle(labelsEl).display === 'none') {
    return false
  }

  const utilBarStyle = window.getComputedStyle(utilBarEl)
  const paddingRight = parseFloat(utilBarStyle.paddingRight)
  const utilBarRect = utilBarEl.getBoundingClientRect()
  const accountRect = accountButtonEl.getBoundingClientRect()

  return accountRect.right > utilBarRect.right - paddingRight + 1
}

function shouldCompactMobileAccount(
  measureEl: HTMLElement,
  accountButtonEl: HTMLElement,
  labelsVisible: boolean,
): boolean {
  if (endClusterOverlapsStart(accountButtonEl)) {
    return true
  }

  if (labelsVisible) {
    return accountSummarySpillsUtilBar(accountButtonEl)
  }

  return !measureExpandedAccountFits(measureEl, accountButtonEl)
}

/**
 * On mobile, hide account name/points when the full account control would overflow the util bar.
 */
function useAutoCompactNavAccountOnMobile(
  measureRef: RefObject<HTMLElement | null>,
  measureDeps: ReadonlyArray<unknown>,
): boolean {
  const [compact, setCompact] = useState(false)
  const measureKey = JSON.stringify(measureDeps)

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`)

    const update = () => {
      if (!mobileQuery.matches) {
        setCompact(false)
        return
      }

      const measureEl = measureRef.current
      const accountButtonEl = toHTMLElement(measureEl?.closest('[data-nav-account]'))
      if (!measureEl || !accountButtonEl) {
        return
      }

      const labelsEl = toHTMLElement(accountButtonEl.querySelector('[data-nav-account-labels]'))
      const labelsVisible = labelsEl != null && window.getComputedStyle(labelsEl).display !== 'none'

      setCompact(shouldCompactMobileAccount(measureEl, accountButtonEl, labelsVisible))
    }

    update()

    const resizeObserver = new ResizeObserver(update)
    const endEl = measureRef.current?.closest('[data-util-bar-end]')
    const utilBarEl = measureRef.current?.closest('[data-util-bar]')
    const startEl = measureRef.current?.closest('[data-util-bar]')?.querySelector('[data-util-bar-start]')

    if (endEl instanceof HTMLElement) {
      resizeObserver.observe(endEl)
    }

    if (utilBarEl instanceof HTMLElement) {
      resizeObserver.observe(utilBarEl)
    }

    if (startEl instanceof HTMLElement) {
      resizeObserver.observe(startEl)
    }

    mobileQuery.addEventListener('change', update)
    window.addEventListener('resize', update)

    return () => {
      resizeObserver.disconnect()
      mobileQuery.removeEventListener('change', update)
      window.removeEventListener('resize', update)
    }
  }, [measureRef, measureKey])

  return compact
}

export default useAutoCompactNavAccountOnMobile
